import { useCallback } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { generateCruiseCheckoutItem } from 'checkout/lib/utils/cruises/cart'
import { trackClientEvent } from 'analytics/analytics'
import {
  addToCheckoutCart,
  removeItem,
  removeTravellerSchema,
} from 'actions/CheckoutActions'
import {
  navigateToCruiseCheckoutStep,
  resetCabinRates,
  setBookingInterestInConnectedCabins,
} from 'actions/CruiseActions'
import { CheckoutPageId } from 'checkout/constants/pages'
import { getCruiseInitialCheckoutStep, getCruiseInitialOccupancy } from 'lib/cruises/cruiseBookingFlow'

export interface UseCruiseStartBookingFlowParams {
  offer: App.CruiseOffer
  cruiseItem: App.Checkout.CruiseItem | undefined
}

export interface UseCruiseStartBookingFlowResult {
  startBookingWithOccupancies: (
    departure: App.CruiseDepartureDetails,
    occupancies: Array<App.Occupants>,
    interestInConnectedCabins: boolean,
  ) => void
  startBookingFlow: (departure: App.CruiseDepartureDetails) => void
}

function useCruiseStartBookingFlow(
  params: UseCruiseStartBookingFlowParams,
): UseCruiseStartBookingFlowResult {
  const { offer, cruiseItem } = params
  const dispatch = useAppDispatch()

  const startBookingWithOccupancies = useCallback((
    departure: App.CruiseDepartureDetails,
    occupancies: Array<App.Occupants>,
    interestInConnectedCabins: boolean,
  ) => {
    if (!departure.departureDate || !departure.arrivalDate) return

    const dates = {
      startDate: departure.departureDate,
      endDate: departure.arrivalDate,
    }

    dispatch(removeTravellerSchema())
    dispatch(resetCabinRates())

    const newCruiseItems = occupancies.map((occupancy) =>
      generateCruiseCheckoutItem({
        version: 'v2',
        offerId: offer.id,
        departureId: departure.id,
        dates,
        occupancy,
      }),
    )

    if (cruiseItem) {
      dispatch(removeItem(cruiseItem.itemId))
    }

    dispatch(addToCheckoutCart(newCruiseItems))
    dispatch(setBookingInterestInConnectedCabins(interestInConnectedCabins))
    trackClientEvent({
      subject: 'select_travellers',
      action: 'clicked',
      category: 'cruises_v2',
      type: 'interaction',
    })
    dispatch(navigateToCruiseCheckoutStep(CheckoutPageId.CruiseCabinType))
  }, [cruiseItem, dispatch, offer.id])

  const startBookingFlow = useCallback((departure: App.CruiseDepartureDetails) => {
    if (!departure.departureDate || !departure.arrivalDate) return

    const dates = {
      startDate: departure.departureDate,
      endDate: departure.arrivalDate,
    }

    const item = generateCruiseCheckoutItem({
      version: 'v2',
      offerId: offer.id,
      departureId: departure.id,
      dates,
      occupancy: getCruiseInitialOccupancy(offer),
    })

    if (cruiseItem) {
      dispatch(removeItem(cruiseItem.itemId))
    }

    dispatch(addToCheckoutCart([item]))
    dispatch(navigateToCruiseCheckoutStep(getCruiseInitialCheckoutStep(offer)))
  }, [cruiseItem, dispatch, offer])

  return {
    startBookingWithOccupancies,
    startBookingFlow,
  }
}

export default useCruiseStartBookingFlow
